<cs-header [fullWidth]="true" [backButton]="false" hideType="auto" [separator]="true">
  <cs-icon-button header-right icon="close" (click)="handleClose()"></cs-icon-button>
  <cs-header-title>
    <span class="header-title-text">{{ LL.mealPlan.title() }}</span>
    @if (dateRangeFormatted) {
    <cs-header-subtitle>{{ dateRangeFormatted }}</cs-header-subtitle>
    }
  </cs-header-title>

  @if (currentDayIndex >= 0) {
  <cs-header-content>
    <div class="meal-plan-stepper-wrapper">
      <cs-stepper [steps]="stepperSteps" [style]="StepperStyle.Traditional"
        (stepClick)="handleStepperClick($event)"></cs-stepper>
    </div>

    @if (tab.meals.length > 0) {
    <!-- Day navigation row -->
    <div class="day-navigation-row">
      <div class="day-selector">
        <cs-icon-button icon="arrow-dropleft" [attr.disabled]="!prevDayEnabled ? true : null"
          (click)="handlePrevDay()"></cs-icon-button>
        <span class="day-label">{{ currentDayFormatted }}</span>
        <cs-icon-button icon="arrow-dropright" [attr.disabled]="!nextDayEnabled ? true : null"
          (click)="handleNextDay()"></cs-icon-button>
      </div>
      <div class="day-actions">
        <cs-button small secondary icon="copy" [disabled]="!hasSelectedMeals" (click)="handleCopyMeals($event)">
          {{ LL.mealPlan.buttons.copyMeals() }}
        </cs-button>
        <cs-button small secondary icon="checkmark" (click)="handleAcceptAllMeals()">
          {{ LL.mealPlan.buttons.acceptAll() }}
        </cs-button>
      </div>
    </div>
    }
  </cs-header-content>
  }
</cs-header>

<!-- Non-day content (loading, empty states, date selection) -->
@if (currentDayIndex < 0) { <cs-content>
  @if (loading) {
  <div class="spinner">
    <cs-spinner></cs-spinner>
    <cs-text-spinner textsId="@help:loading-hints.food"></cs-text-spinner>
  </div>
  }

    <!-- Select dates state -->
    @if (currentDayIndex < 0 && !loading && (validDateMin || validDateMax)) { <cs-card-list>
      <cs-card>
        <cs-empty-state class="select-dates" iconName="calendar-clock"
          [title]="LL.mealPlan.emptyState.selectDatesTitle()" [subtext]="LL.mealPlan.emptyState.selectDatesSubtext()">
          <div class="date-selection-row">
            <div class="date-selection-field">
              <cs-label>{{ LL.mealPlan.labels.startDate() }}</cs-label>
              <input-calendar [(date)]="firstDayString" [minDate]="validDateMin" [maxDate]="validDateMax"
                (dateChange)="handleStartDateChanged()">
              </input-calendar>
            </div>
            <div class="date-selection-field">
              <cs-label>{{ LL.mealPlan.labels.numberOfDays() }}</cs-label>
              <cs-select [(value)]="selectedDuration" (valueChange)="handleDurationChanged()">
                @for (option of durationOptions; track option.value) {
                <cs-select-option [value]="option.value" [label]="option.label">
                </cs-select-option>
                }
              </cs-select>
            </div>
          </div>
          <cs-button small [disabled]="!datesCorrect" icon="wand" (click)="handleStartMealPlanning()">
            {{ LL.mealPlan.buttons.generateMeals() }}
          </cs-button>
        </cs-empty-state>
      </cs-card>
      </cs-card-list>
      }
      </cs-content>
      }

      <!-- Day content with animated transitions (A/B recycling slots) -->
      @if (currentDayIndex >= 0) {
      <cs-tab-content-container class="step-content-container" [selection]="tabSlot" [isBack]="isNavigatingBack"
        [swipeEnabled]="hasClientSmallScreen()" (swipeForward)="handleSwipeForward()" (swipeBack)="handleSwipeBack()">
        <cs-tab-content [value]="'a'">
          <cs-content [loading]="tabLoading.a">
            <ng-container *ngTemplateOutlet="dayContent; context: { data: tabData.a }"></ng-container>
          </cs-content>
        </cs-tab-content>
        <cs-tab-content [value]="'b'">
          <cs-content [loading]="tabLoading.b">
            <ng-container *ngTemplateOutlet="dayContent; context: { data: tabData.b }"></ng-container>
          </cs-content>
        </cs-tab-content>
      </cs-tab-content-container>
      }

      <!-- Shared day content template (receives per-tab data via context) -->
      <ng-template #dayContent let-data="data">
        @if (data.meals.length > 0) {
        <cs-card-list>
          <!-- Daily macro targets card -->
          <cs-card class="day-info">
            <cs-card-title>
              {{ LL.mealPlan.dailyMacroTargets() }}
            </cs-card-title>
            <cs-card-content>
              <div class="macro-targets-content">
                <nutrient-list [nutrients]="data.displayNutrientsDay" state="linear-card" [expandable]="true"
                  [match_pc]="data.match_pc">
                </nutrient-list>
              </div>
            </cs-card-content>
          </cs-card>

          <!-- Meal sections -->
          @for (meal of data.meals; track meal.mealLog._id) {
          <cs-card-list-row class="meal-time-header">
            <span class="meal-time-label">{{ mealDisplayName(meal) }} - {{ meal.time }}</span>
            <div class="meal-time-divider"></div>
          </cs-card-list-row>
          <cs-food-card [food]="meal.food" [matchPercent]="getMealMatchPercent(meal)"
            [description]="meal.shortDescription" [nutrients]="meal.displayNutrients"
            [showMenuButton]="true" [menuInTitle]="true" [loading]="meal.loading"
            (menuClick)="handleMealMenu($event, meal)" (click)="handleMealView(meal.food)">
            <div card-actions class="card-action-buttons">
              <cs-icon-button icon="copy" (click)="handleCopyMeal(meal, $event)"></cs-icon-button>
              <cs-icon-button icon="swap-horizontal" (click)="handlePickMeal(meal, $event)"></cs-icon-button>
              <cs-icon-button [icon]="meal.isSelected ? 'checkmark' : 'circle'" [class.selected]="meal.isSelected"
                (click)="handleToggleMealSelection(meal, $event)"></cs-icon-button>
            </div>
          </cs-food-card>
          <cs-card-list-row class="meal-actions">
            @if (meal.food && !isLogMealById(meal.food._id)) {
            <cs-button tertiary icon="liked" [fillIcon]="isFavorite(meal)" (click)="voteAsync(meal.food, +1)">{{
              LL.mealPlan.buttons.favorite() }}</cs-button>
            @if (!isFavorite(meal)) {
            <cs-button tertiary icon="disliked" (click)="voteAsync(meal.food, -1)">{{
              LL.mealPlan.buttons.dontSuggest()
              }}</cs-button>
            }
            }
          </cs-card-list-row>
          }
        </cs-card-list>
        }
      </ng-template>

      <!-- Page action buttons (Finish) -->
      @if (currentDayIndex >= 0) {
      <cs-page-action-buttons>
        <cs-button primary [disabled]="nextDayEnabled" (click)="handleDone()">
          {{ LL.common.buttons.finish() }}
        </cs-button>
      </cs-page-action-buttons>
      }